import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab
} from '@mui/material';
import {
  PlayArrow,
  CheckCircle,
  RadioButtonUnchecked,
  Quiz,
  Description,
  VideoLibrary,
  ExpandMore,
  Refresh,
  School,
  LibraryBooks,
  Assessment
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function StudentDashboard() {
  const { user, logout } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseModules, setCourseModules] = useState([]);
  const [moduleContent, setModuleContent] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [userProgress, setUserProgress] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Диалоги
  const [contentDialog, setContentDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [testAnswers, setTestAnswers] = useState({});
  const [testSubmitting, setTestSubmitting] = useState(false);

  useEffect(() => {
    loadStudentData();
  }, []);

  const loadStudentData = async () => {
    try {
      console.log('=== STUDENT DASHBOARD: LOADING DATA ===');
      setLoading(true);

      // Загружаем курсы
      try {
        console.log('=== LOADING COURSES ===');
        const coursesRes = await api.get('/courses');
        console.log('=== COURSES RESPONSE ===', coursesRes.data);
        setCourses(coursesRes.data);
      } catch (coursesError) {
        console.error('Error loading courses:', coursesError);
        setCourses([]);
      }

      // Загружаем прогресс
      try {
        console.log('=== LOADING PROGRESS ===');
        const progressRes = await api.get('/courses/user/progress');
        console.log('=== PROGRESS RESPONSE ===', progressRes.data);
        setUserProgress(progressRes.data);
      } catch (progressError) {
        console.error('Error loading progress:', progressError);
        setUserProgress([]);
      }

      // Загружаем результаты тестов
      try {
        console.log('=== LOADING TEST RESULTS ===');
        const resultsRes = await api.get('/tests/results');
        console.log('=== TEST RESULTS RESPONSE ===', resultsRes.data);
        setTestResults(resultsRes.data);
      } catch (resultsError) {
        console.error('Error loading test results:', resultsError);
        setTestResults([]);
      }

    } catch (error) {
      console.error('General error in loadStudentData:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCourseModules = async (courseId) => {
    try {
      const response = await api.get(`/courses/${courseId}/modules`);
      setCourseModules(response.data);
    } catch (error) {
      console.error('Error loading modules:', error);
    }
  };

  const loadModuleContent = async (courseId, moduleId) => {
    try {
      const response = await api.get(`/courses/${courseId}/modules/${moduleId}/content`);
      setModuleContent(response.data);
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    loadCourseModules(course.id);
    setSelectedModule(null);
    setModuleContent([]);
  };

  const handleModuleSelect = (module) => {
    setSelectedModule(module);
    loadModuleContent(selectedCourse.id, module.id);
  };

  const handleContentOpen = (content) => {
    setSelectedContent(content);
    setContentDialog(true);
  };

  const handleTestStart = async (module) => {
    try {
      // Получаем все тесты и ищем нужный по module_id
      const testResponse = await api.get('/tests');

      const moduleTest = testResponse.data.find(test => test.module_id === module.id);

      if (moduleTest) {
        const fullTest = await api.get(`/tests/${moduleTest.id}`);
        setCurrentTest(fullTest.data);
        setTestAnswers({});
        setTestDialog(true);
      } else {
        alert('Для этого модуля тест еще не создан.');
      }
    } catch (error) {
      console.error('Error loading test:', error);
      alert('Ошибка при загрузке теста.');
    }
  };

  const handleTestSubmit = async () => {
    try {
      setTestSubmitting(true);

      // Преобразуем объект testAnswers в массив для backend
      const answersArray = Object.entries(testAnswers).map(([questionId, answerId]) => ({
        question_id: parseInt(questionId),
        answer_id: parseInt(answerId)
      }));

      console.log('=== SUBMITTING TEST ===');
      console.log('Current test:', currentTest);
      console.log('Test answers object:', testAnswers);
      console.log('Answers array:', answersArray);

      const response = await api.post(`/tests/${currentTest.id}/submit`, {
        answers: answersArray
      });

      alert(`Тест завершен! Ваш результат: ${response.data.score}% (${response.data.correctAnswers}/${response.data.totalQuestions} правильных ответов)`);

      console.log('=== TEST COMPLETED SUCCESSFULLY ===');
      console.log('Test result:', response.data);

      setTestDialog(false);
      setCurrentTest(null);
      setTestAnswers({});

      // Небольшая задержка перед обновлением данных
      setTimeout(() => {
        console.log('=== RELOADING STUDENT DATA ===');
        loadStudentData(); // Обновляем прогресс
      }, 500);
    } catch (error) {
      console.error('Error submitting test:', error);
      alert('Ошибка при отправке теста.');
    } finally {
      setTestSubmitting(false);
    }
  };

  const getCourseProgress = (courseId) => {
    const courseModulesProgress = userProgress.filter(p => p.course_id === courseId);
    if (courseModulesProgress.length === 0) return 0;

    const completedModules = courseModulesProgress.filter(p => p.completed).length;
    return Math.round((completedModules / courseModulesProgress.length) * 100);
  };

  const isModuleCompleted = (moduleId) => {
    return userProgress.some(p => p.module_id === moduleId && p.completed);
  };

  const getTestResult = (moduleId) => {
    return testResults.find(r => r.module_id === moduleId);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Кабинет слушателя
          </Typography>
          <Button variant="outlined" onClick={logout}>
            Выйти
          </Button>
        </Box>

        {/* Статистика обучения */}
        {/* <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Доступных курсов
                </Typography>
                <Typography variant="h4">
                  {courses.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Пройденных модулей
                </Typography>
                <Typography variant="h4">
                  {userProgress.filter(p => p.completed).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
         
        </Grid> */}

        <Grid container spacing={3}>
          {/* Список курсов */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Доступные курсы</Typography>
                <IconButton onClick={loadStudentData}>
                  <Refresh />
                </IconButton>
              </Box>

              <List>
                {courses.map((course) => (
                  <ListItem
                    key={course.id}
                    button
                    selected={selectedCourse?.id === course.id}
                    onClick={() => handleCourseSelect(course)}
                  >
                    <ListItemText
                      primary={course.title}
                      secondary={
                        <React.Fragment>
                          <Box component="span" display="block">
                            {course.description}
                          </Box>
                          <Box component="span" display="block" sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={getCourseProgress(course.id)}
                              sx={{ height: 4, mb: 0.5 }}
                            />
                            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              Прогресс: {getCourseProgress(course.id)}%
                            </Box>
                          </Box>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Рабочая область */}
          <Grid item xs={12} md={8}>
            {selectedCourse ? (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedCourse.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedCourse.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Модули курса
                </Typography>

                {courseModules.map((module) => (
                  <Accordion key={module.id} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" alignItems="center" width="100%">
                        <Typography sx={{ flexGrow: 1 }}>
                          {module.title}
                        </Typography>
                        {isModuleCompleted(module.id) ? (
                          <CheckCircle color="success" sx={{ mr: 1 }} />
                        ) : (
                          <RadioButtonUnchecked color="disabled" sx={{ mr: 1 }} />
                        )}
                        <Chip
                          label={`${module.content_count} элементов`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {module.description}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedModule(module);
                            loadModuleContent(selectedCourse.id, module.id);
                          }}
                          sx={{ mr: 1 }}
                        >
                          Просмотреть контент
                        </Button>

                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Quiz />}
                          onClick={() => handleTestStart(module)}
                          disabled={false} // Разрешаем повторное прохождение
                        >
                          {getTestResult(module.id) ? 'Пересдать тест' : 'Пройти тест'}
                        </Button>
                      </Box>

                      {/* Контент модуля */}
                      {selectedModule?.id === module.id && moduleContent.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Материалы модуля:
                          </Typography>
                          <List dense>
                            {moduleContent.map((content) => (
                              <ListItem key={content.id} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={content.title}
                                  secondary={`Тип: ${content.content_type}`}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleContentOpen(content)}
                                >
                                  {content.content_type === 'video' && <VideoLibrary sx={{ mr: 0.5 }} />}
                                  {content.content_type === 'document' && <Description sx={{ mr: 0.5 }} />}
                                  {content.content_type === 'text' && <Description sx={{ mr: 0.5 }} />}
                                  Просмотреть
                                </Button>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}

                      {getTestResult(module.id) && (
                        <Alert
                          severity={getTestResult(module.id).passed ? 'success' : 'warning'}
                          sx={{ mt: 2 }}
                        >
                          Результат теста: {getTestResult(module.id).score}% -
                          {getTestResult(module.id).passed ? ' Пройден' : ' Не пройден'}
                        </Alert>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <School sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Выберите курс для изучения
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Выберите курс из списка слева, чтобы начать обучение
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Диалог просмотра контента */}
        <Dialog
          open={contentDialog}
          onClose={() => setContentDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedContent?.title}
          </DialogTitle>
          <DialogContent>
            {selectedContent?.content_type === 'text' && (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedContent.content}
              </Typography>
            )}

            {selectedContent?.content_type === 'video' && selectedContent.file_path && (
              <Box>
                <video controls style={{ width: '100%', maxHeight: '400px' }}>
                  <source src={`/uploads/${selectedContent.file_path.split('/').pop()}`} />
                  Ваш браузер не поддерживает видео.
                </video>
              </Box>
            )}

            {selectedContent?.content_type === 'document' && selectedContent.file_path && (
              <Box>
                

                {/* Для PDF файлов - встроенный просмотр */}
                {selectedContent.file_name.toLowerCase().endsWith('.pdf') && (
                  <Box sx={{ height: '600px', border: '1px solid #ddd', borderRadius: 1 }}>
                    <iframe
                      src={`/uploads/${selectedContent.file_path.split('/').pop()}`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none', borderRadius: '4px' }}
                      title={`Просмотр документа: ${selectedContent.file_name}`}
                    />
                  </Box>
                )}

                {/* Для документов Office - Google Docs Viewer */}
                {(selectedContent.file_name.toLowerCase().endsWith('.doc') ||
                  selectedContent.file_name.toLowerCase().endsWith('.docx') ||
                  selectedContent.file_name.toLowerCase().endsWith('.xls') ||
                  selectedContent.file_name.toLowerCase().endsWith('.xlsx') ||
                  selectedContent.file_name.toLowerCase().endsWith('.ppt') ||
                  selectedContent.file_name.toLowerCase().endsWith('.pptx')) && (
                  <Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        href={`/uploads/${selectedContent.file_path.split('/').pop()}`}
                        target="_blank"
                        startIcon={<Description />}
                      >
                        Скачать
                      </Button>
                    </Box>
                    <Box sx={{ height: '600px', border: '1px solid #ddd', borderRadius: 1 }}>
                      <iframe
                        src={`https://docs.google.com/viewer?url=${window.location.origin}/uploads/${selectedContent.file_path.split('/').pop()}&embedded=true`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', borderRadius: '4px' }}
                        title={`Просмотр документа: ${selectedContent.file_name}`}
                      />
                    </Box>
                  </Box>
                )}

                {/* Для других документов - кнопка скачивания */}
                {!(selectedContent.file_name.toLowerCase().endsWith('.pdf') ||
                   selectedContent.file_name.toLowerCase().endsWith('.doc') ||
                   selectedContent.file_name.toLowerCase().endsWith('.docx') ||
                   selectedContent.file_name.toLowerCase().endsWith('.xls') ||
                   selectedContent.file_name.toLowerCase().endsWith('.xlsx') ||
                   selectedContent.file_name.toLowerCase().endsWith('.ppt') ||
                   selectedContent.file_name.toLowerCase().endsWith('.pptx')) && (
                  <Box textAlign="center" sx={{ py: 4 }}>
                    <Description sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Этот тип документа не поддерживает предварительный просмотр в браузере
                    </Typography>
                    <Button
                      variant="contained"
                      href={`/uploads/${selectedContent.file_path.split('/').pop()}`}
                      target="_blank"
                      startIcon={<Description />}
                    >
                      Скачать документ
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContentDialog(false)}>Закрыть</Button>
          </DialogActions>
        </Dialog>

        {/* Диалог прохождения теста */}
        <Dialog
          open={testDialog}
          onClose={() => setTestDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Тест: {currentTest?.title}
          </DialogTitle>
          <DialogContent>
            {currentTest?.description && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {currentTest.description}
              </Typography>
            )}

            <Alert severity="info" sx={{ mb: 2 }}>
              Проходной балл: {currentTest?.passing_score}% | Вопросов: {currentTest?.questions?.length}
            </Alert>

            {currentTest?.questions?.map((question, qIndex) => (
              <Box key={question.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {qIndex + 1}. {question.question_text}
                </Typography>

                {question.answers.map((answer) => (
                  <Box key={answer.id} sx={{ ml: 2, mb: 1 }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={answer.id}
                        checked={testAnswers[question.id] === answer.id}
                        onChange={(e) => setTestAnswers({
                          ...testAnswers,
                          [question.id]: parseInt(e.target.value)
                        })}
                        style={{ marginRight: 8 }}
                      />
                      <Typography variant="body2">
                        {answer.answer_text}
                      </Typography>
                    </label>
                  </Box>
                ))}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestDialog(false)}>Отмена</Button>
            <Button
              onClick={handleTestSubmit}
              variant="contained"
              disabled={testSubmitting || !currentTest?.questions?.every(q => testAnswers[q.id])}
            >
              {testSubmitting ? <CircularProgress size={20} /> : 'Завершить тест'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}

export default StudentDashboard;
